import { BlogArticle, BlogArticleKey } from "@/app/types/BlogArticle";
import { ArticleBlockType } from "@/app/types/BlogArticleContent";
import { BlogSlugs, ProtectedPaths } from "@/app/vars/Routes";

export const modjoAlternativesArticle: BlogArticle = {
  key: BlogArticleKey.MODJO_ALTERNATIVES,
  slug: BlogSlugs.MODJO_ALTERNATIVES,
  categoryId: "01",
  date: "2026-08-27",
  readTime: 9,
  locales: {
    fr: {
      title: "Alternative à Modjo : les options selon ta taille d'équipe",
      excerpt:
        "Modjo est un bon produit. Ce qui bloque est rarement sa qualité, c'est son prix et son plancher de sièges. Voici les alternatives réelles, et la question que personne ne pose avant de choisir.",
      description:
        "Modjo est bien noté et ce n'est pas le sujet. Si tu cherches une alternative, c'est souvent une question de budget ou de taille d'équipe. Tour d'horizon honnête des options, avec les prix, les limites, et le critère qui compte vraiment.",
      ctaTitle: "Et si le problème n'était pas l'outil d'analyse ?",
      ctaText:
        "Teste sur tes propres appels ce que change une donnée qui circule au lieu de mourir dans un compte rendu. Sans engagement, sans carte bancaire.",
      body: [
        {
          type: ArticleBlockType.LEAD,
          text: "Personne ne cherche une alternative à un outil qui marche mal. On cherche une alternative à un outil trop cher, trop lourd, ou dimensionné pour une équipe qu'on n'a pas.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Commençons par le dire clairement : **Modjo est un bon produit**. Il est noté autour de 4,7 sur G2 et 4,5 sur Capterra, ce qui n'est pas le score d'un outil qui déçoit. Les utilisateurs citent la relecture d'appels, la bibliothèque organisée par sujet et le partage des bonnes pratiques.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si tu es ici, ce n'est donc probablement pas parce que Modjo est mauvais. C'est parce qu'il n'est pas fait pour ta situation.",
        },
        { type: ArticleBlockType.H2, text: "Pourquoi on cherche une alternative à Modjo" },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**La tarification est sur devis.** Pas de grille publique, donc aucun moyen de savoir si c'est pour toi avant d'avoir parlé à un commercial.",
            "**Il y a un plancher de sièges.** Modjo se positionne sur des équipes commerciales structurées, pas sur un fondateur seul ou un duo.",
            "**L'engagement est annuel.** Difficile de tester six semaines pour voir.",
            "**Le produit est pensé pour le management d'équipe.** Revue d'appels, coaching, comparaison entre commerciaux. Si tu n'as personne à coacher, tu payes pour une brique que tu n'ouvriras jamais.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les montants qui circulent publiquement tournent autour de 99 € par utilisateur et par mois, avec un minimum d'équipe. Les sources ne s'accordent pas sur ce minimum, et la grille n'étant pas publiée, prends ces chiffres comme un ordre de grandeur à vérifier, pas comme une référence.",
        },
        { type: ArticleBlockType.H2, text: "Les alternatives, classées par besoin réel" },
        {
          type: ArticleBlockType.TOOL,
          name: "Leexi",
          tag: "Le plus proche, moins cher",
          desc: "Analyse conversationnelle avec transcription, résumés et détection de sujets. Positionnement européen, prise en main rapide, approche multilingue solide. C'est l'alternative la plus directe si tu veux la même famille d'outil sans le ticket d'entrée.",
          limit:
            "on reste dans la même logique : l'outil analyse tes appels et s'arrête là. Ce que tu fais des objections détectées reste ton problème.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Claap",
          tag: "Prix public, notation par framework",
          desc: "Note automatiquement chaque appel selon ton framework de vente, MEDDIC, SPICED ou le tien. Grille tarifaire publiée, autour de 32 $ et 60 $ par utilisateur et par mois selon le plan, ce qui permet de décider sans passer par un commercial.",
          limit:
            "la notation par framework suppose que tu en aies un, et qu'il soit stable. Sur un cycle de vente encore mouvant, la note dit peu de choses.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "tl;dv",
          tag: "Le plus léger",
          desc: "Transcription, résumés, extraits vidéo partageables. Un palier gratuit existe. Parfait si ton besoin réel est de garder une trace propre et de ne rien oublier d'une réunion.",
          limit:
            "il capture, il ne creuse pas. Aucune lecture transversale de ce qui revient d'un appel à l'autre.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Fathom",
          tag: "Gratuit pour commencer",
          desc: "Notetaker efficace avec une offre gratuite généreuse. Le bon premier pas quand on veut arrêter de prendre des notes à la main sans rien engager.",
          limit: "même limite que tl;dv, et une analyse commerciale de fond quasi absente.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Gong",
          tag: "L'inverse du problème",
          desc: "La référence du revenue intelligence, avec prévisions et pilotage du pipeline.",
          limit:
            "si tu trouves Modjo trop lourd et trop cher, Gong est un cran au-dessus sur les deux. Ce n'est pas une alternative, c'est une montée en gamme.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Outil", "Pour qui, et à quel prix"],
          rows: [
            ["Modjo", "Équipe structurée avec un manager. Sur devis, engagement annuel."],
            ["Leexi", "Même besoin, budget plus contenu. Autour de 39 € par utilisateur."],
            ["Claap", "Ceux qui veulent un prix public. Environ 32 $ à 60 $ par utilisateur."],
            ["tl;dv", "Garder une trace propre. Palier gratuit disponible."],
            ["Fathom", "Débuter sans rien payer. Offre gratuite."],
            ["Gong", "Grandes équipes avec pilotage du pipeline. Nettement au-dessus."],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Les tarifs ci-dessus sont ceux publiés ou rapportés au moment où cet article est écrit. Vérifie chez l'éditeur avant de décider, ils bougent.",
        },
        { type: ArticleBlockType.H2, text: "La question que personne ne pose avant de choisir" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tous ces outils répondent à la même question : *qu'est-ce qui s'est dit pendant cet appel ?* Aucun ne répond à la suivante, qui est pourtant celle qui change ton quotidien : *qu'est-ce que j'en fais ?*",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Tu ressors avec une objection identifiée dans onze conversations. Très bien. Elle finit dans un compte rendu que personne ne rouvre, pendant que tu réécris tes messages de prospection à l'intuition, dans un autre onglet.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Analyser pour analyser",
            items: [
              "Un compte rendu par appel, qu'on relit rarement",
              "Les objections restent dans l'outil d'analyse",
              "Le ciblage et les messages se réécrivent à l'intuition",
              "Chaque nouvel appel repart d'une page blanche",
            ],
          },
          good: {
            title: "Exploiter ce qui a été dit",
            items: [
              "Les signaux s'accumulent dans une même base",
              "Les objections nourrissent le portrait de qui achète",
              "Ce portrait devient des critères de ciblage",
              "Ces critères deviennent des angles de message",
            ],
          },
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "C'est exactement là que Meidly se place",
          text: "Et il faut être honnête : Meidly ne remplace pas Modjo. Il ne cherche pas à être le meilleur outil pour relire un appel ou coacher un commercial. Il part de ce qui se dit dans tes échanges pour construire ton avatar client, en tirer des critères de ciblage, puis des messages. La donnée est capturée une fois et ressert tout au long de la chaîne, au lieu de mourir dans un compte rendu.",
          linkLabel: "Voir l'analyse des appels de Meidly",
          linkPath: ProtectedPaths.CALL_ANALYSIS,
        },
        { type: ArticleBlockType.H2, text: "Alors, laquelle choisir ?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si tu manages une équipe et que le budget suit, **reste sur Modjo**. Chercher une alternative te fera perdre plus que tu ne gagneras.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si le besoin est le même mais que le ticket d'entrée bloque, regarde **Leexi** ou **Claap**, ce dernier ayant l'avantage d'un prix affiché.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Si tu veux surtout arrêter de prendre des notes, **Fathom** ou **tl;dv** suffisent, et coûtent zéro pour commencer.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Et si ton vrai problème n'est pas d'analyser tes appels mais de **savoir quoi en faire ensuite**, aucun de ces outils ne le réglera, quel que soit son prix. C'est une autre catégorie de produit.",
        },
      ],
      faq: [
        {
          question: "Combien coûte Modjo ?",
          answer:
            "Modjo ne publie pas de grille tarifaire, la tarification se fait sur devis avec un engagement annuel et un nombre minimum de sièges. Les montants rapportés publiquement tournent autour de 99 € par utilisateur et par mois, mais en l'absence de grille officielle, considère cela comme un ordre de grandeur à confirmer auprès de l'éditeur.",
        },
        {
          question: "Quelle est la meilleure alternative gratuite à Modjo ?",
          answer:
            "Fathom et tl;dv proposent tous deux un palier gratuit. Ce ne sont pas des équivalents fonctionnels : ils transcrivent et résument, sans l'analyse transversale ni les fonctions de coaching de Modjo. Pour garder une trace de tes réunions sans rien payer, ils suffisent largement.",
        },
        {
          question: "Leexi ou Claap, lequel remplace le mieux Modjo ?",
          answer:
            "Leexi est le plus proche fonctionnellement, avec un positionnement européen et un tarif autour de 39 € par utilisateur. Claap se distingue par une grille publique, autour de 32 $ à 60 $ selon le plan, et par la notation automatique de chaque appel selon ton framework de vente. Si tu veux décider sans passer par un commercial, Claap a l'avantage.",
        },
        {
          question: "Meidly est-il une alternative à Modjo ?",
          answer:
            "Non, et le prétendre serait malhonnête. Modjo sert à relire, analyser et coacher sur des appels. Meidly part de ce qui se dit dans tes échanges pour construire ton avatar client, tes critères de ciblage et tes messages. Les deux peuvent coexister : l'un regarde l'appel, l'autre exploite ce qu'il contient.",
        },
        {
          question: "Faut-il vraiment un outil payant pour analyser ses appels ?",
          answer:
            "Non, si tu as peu d'appels. Coller une transcription dans ChatGPT ou Claude donne souvent un bon résultat sur une conversation isolée. La limite apparaît avec le volume : une IA généraliste repart de zéro à chaque fois, ne garde rien d'un appel à l'autre et t'oblige à tout réexpliquer.",
        },
      ],
    },
    en: {
      title: "Modjo alternatives: the options that fit your team size and budget",
      excerpt:
        "Modjo is a good product. What blocks people is rarely its quality, it is the price and the seat floor. Here are the real alternatives, and the question nobody asks before choosing.",
      description:
        "Modjo is well rated and that is not the issue. If you are looking for an alternative, it is usually a budget or team size question. An honest tour of the options, with prices, limits, and the criterion that actually matters.",
      ctaTitle: "What if the analysis tool was not the problem?",
      ctaText:
        "See on your own calls what changes when the data travels instead of dying in a summary. No commitment, no credit card.",
      body: [
        {
          type: ArticleBlockType.LEAD,
          text: "Nobody looks for an alternative to a tool that works badly. People look for an alternative to a tool that is too expensive, too heavy, or sized for a team they do not have.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "Let us say it plainly first: **Modjo is a good product**. It sits around 4.7 on G2 and 4.5 on Capterra, which is not the score of a tool that disappoints. Users point to call replay, the library organised by topic, and the sharing of best practices.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "So if you are here, it is probably not because Modjo is bad. It is because it is not built for your situation.",
        },
        { type: ArticleBlockType.H2, text: "Why people look for a Modjo alternative" },
        {
          type: ArticleBlockType.LIST,
          items: [
            "**Pricing is quote only.** No public grid, so no way to know whether it fits you before talking to a sales rep.",
            "**There is a seat floor.** Modjo targets structured sales teams, not a solo founder or a pair.",
            "**The commitment is annual.** Hard to try it for six weeks and see.",
            "**The product is built for team management.** Call review, coaching, rep comparison. With nobody to coach, you pay for a module you will never open.",
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The figures circulating publicly sit around 99 euros per user per month, with a team minimum. Sources disagree on that minimum, and since the grid is not published, treat these numbers as an order of magnitude to verify, not as a reference.",
        },
        { type: ArticleBlockType.H2, text: "The alternatives, sorted by actual need" },
        {
          type: ArticleBlockType.TOOL,
          name: "Leexi",
          tag: "Closest, cheaper",
          desc: "Conversation analysis with transcription, summaries and topic detection. European positioning, quick onboarding, solid multilingual approach. This is the most direct alternative if you want the same family of tool without the entry ticket.",
          limit:
            "you stay in the same logic: the tool analyses your calls and stops there. What you do with the objections it finds is still your problem.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Claap",
          tag: "Public pricing, framework scoring",
          desc: "Automatically scores every call against your sales framework, MEDDIC, SPICED or your own. Published pricing, around 32 and 60 dollars per user per month depending on the plan, so you can decide without going through a sales rep.",
          limit:
            "framework scoring assumes you have one, and that it is stable. On a sales cycle that is still moving, the score says very little.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "tl;dv",
          tag: "The lightest",
          desc: "Transcription, summaries, shareable video clips. A free tier exists. Perfect if your real need is keeping a clean record and forgetting nothing from a meeting.",
          limit:
            "it captures, it does not dig. No cross reading of what comes back from one call to the next.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Fathom",
          tag: "Free to start",
          desc: "An effective notetaker with a generous free plan. The right first step when you want to stop taking notes by hand without committing to anything.",
          limit: "same limit as tl;dv, and almost no deep commercial analysis.",
        },
        {
          type: ArticleBlockType.TOOL,
          name: "Gong",
          tag: "The opposite problem",
          desc: "The revenue intelligence reference, with forecasting and pipeline steering.",
          limit:
            "if you find Modjo too heavy and too expensive, Gong is a notch above on both. It is not an alternative, it is a step up.",
        },
        {
          type: ArticleBlockType.TABLE,
          head: ["Tool", "Who it is for, and at what price"],
          rows: [
            ["Modjo", "Structured team with a manager. Quote based, annual commitment."],
            ["Leexi", "Same need, tighter budget. Around 39 euros per user."],
            ["Claap", "Those who want a public price. Roughly 32 to 60 dollars per user."],
            ["tl;dv", "Keeping a clean record. Free tier available."],
            ["Fathom", "Starting without paying. Free plan."],
            ["Gong", "Large teams steering a pipeline. Clearly above."],
          ],
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "The prices above are those published or reported at the time this article was written. Check with the vendor before deciding, they move.",
        },
        { type: ArticleBlockType.H2, text: "The question nobody asks before choosing" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "All these tools answer the same question: *what was said during this call?* None of them answers the next one, which is the one that changes your day to day: *what do I do with it?*",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "You come out with an objection identified in eleven conversations. Good. It ends up in a summary nobody reopens, while you rewrite your prospecting messages from intuition, in another tab.",
        },
        {
          type: ArticleBlockType.COMPARE,
          bad: {
            title: "Analysing for the sake of analysing",
            items: [
              "One summary per call, rarely reread",
              "Objections stay inside the analysis tool",
              "Targeting and messages get rewritten from intuition",
              "Every new call starts from a blank page",
            ],
          },
          good: {
            title: "Using what was actually said",
            items: [
              "Signals accumulate in a single base",
              "Objections feed the portrait of who buys",
              "That portrait becomes targeting criteria",
              "Those criteria become message angles",
            ],
          },
        },
        {
          type: ArticleBlockType.CALLOUT,
          title: "This is exactly where Meidly sits",
          text: "And let us be honest: Meidly does not replace Modjo. It does not try to be the best tool to replay a call or coach a rep. It starts from what is said in your conversations to build your client avatar, turn it into targeting criteria, then into messages. The data is captured once and serves the whole chain, instead of dying in a summary.",
          linkLabel: "See Meidly call analysis",
          linkPath: ProtectedPaths.CALL_ANALYSIS,
        },
        { type: ArticleBlockType.H2, text: "So which one should you pick?" },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If you manage a team and the budget follows, **stay on Modjo**. Looking for an alternative will cost you more than it saves.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If the need is the same but the entry ticket blocks you, look at **Leexi** or **Claap**, the latter having the advantage of a published price.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "If you mostly want to stop taking notes, **Fathom** or **tl;dv** are enough, and cost nothing to start.",
        },
        {
          type: ArticleBlockType.PARAGRAPH,
          text: "And if your real problem is not analysing your calls but **knowing what to do with them next**, none of these tools will solve it, whatever the price. That is a different category of product.",
        },
      ],
      faq: [
        {
          question: "How much does Modjo cost?",
          answer:
            "Modjo does not publish a pricing grid, pricing is quote based with an annual commitment and a minimum number of seats. Publicly reported figures sit around 99 euros per user per month, but with no official grid, treat that as an order of magnitude to confirm with the vendor.",
        },
        {
          question: "What is the best free alternative to Modjo?",
          answer:
            "Fathom and tl;dv both offer a free tier. They are not functional equivalents: they transcribe and summarise, without the cross call analysis or the coaching features of Modjo. To keep a record of your meetings without paying, they are more than enough.",
        },
        {
          question: "Leexi or Claap, which one replaces Modjo best?",
          answer:
            "Leexi is the closest functionally, with a European positioning and a price around 39 euros per user. Claap stands out with a public grid, roughly 32 to 60 dollars depending on the plan, and automatic scoring of every call against your sales framework. If you want to decide without talking to a sales rep, Claap has the edge.",
        },
        {
          question: "Is Meidly a Modjo alternative?",
          answer:
            "No, and claiming otherwise would be dishonest. Modjo is there to replay, analyse and coach on calls. Meidly starts from what is said in your conversations to build your client avatar, your targeting criteria and your messages. The two can coexist: one looks at the call, the other uses what it contains.",
        },
        {
          question: "Do you really need a paid tool to analyse your calls?",
          answer:
            "Not if you have few calls. Pasting a transcript into ChatGPT or Claude often gives a good result on a single conversation. The limit shows up with volume: a general purpose AI starts from scratch every time, keeps nothing from one call to the next, and forces you to re explain everything.",
        },
      ],
    },
  },
};
